/**
 * PICNIC Field SE/SU 디자인 토큰.
 * 출처: FIELD-SCR-001 v0.5·v0.6 "SE/SU 모드 · 디자인 토큰 규격" + 와이어프레임 공통 스타일.
 *  - Campus 디자인 토큰 소비(초록 #17332B/#1F7A4A/#2E9E63 · 포인트 #FFC845) — Bench와 동일.
 *  - SE: 터치 타깃 ≥64px(리모컨 ≥96px) · 아이콘 우선 라벨 · 피코 말풍선 = 읽어주기 대상.
 *  - SU: 표준 밀도(≥44px) · 텍스트 라벨.
 * Campus 토큰 패키지가 공개되면 colors 는 그쪽 값으로 교체한다(값 자체는 같아야 한다).
 */

export type Density = "se" | "su";

/**
 * 테마 토큰 — **Bench 와 같은 이름·같은 값**(picnic-bench apps/bench-web/src/bridge/theme.ts @62f4f03 DEFAULT_TOKENS).
 * SCR v0.6 "토큰: Campus 디자인 토큰 소비 … — Bench와 동일". host(Campus)가 같은 이름으로 덮어쓸 수 있다({@link applyTokens}).
 * 앞 6개가 Bench 원본이고, 나머지는 Field 화면이 더 필요로 하는 토큰을 Bench 명명 규칙(color-*, radius-*)으로 보탠 것이다.
 */
export const DEFAULT_TOKENS: Readonly<Record<string, string>> = {
  // ── Bench 원본(이름·값 동일)
  "color-ink": "#17332B",
  "color-primary": "#1F7A4A",
  "color-accent": "#2E9E63",
  "color-point": "#FFC845",
  "color-surface": "#F4F5F0",
  "radius-md": "12px",
  // ── Field 추가(같은 규칙)
  "color-point-ink": "#5A4408",
  "color-surface-tab": "#EAF4EE",
  "color-card": "#FFFFFF",
  "color-surface-dim": "#F3F6F2",
  "color-border": "#D8E5DC",
  "color-ink-muted": "#5B7168",
  "color-scene": "#CFE3D5",
  "color-selected": "#FFF9EA",
  "color-ok-bg": "#DBF1E3",
  "color-ok-ink": "#0C5A3C",
  "color-ok-border": "#A9D8BC",
  "color-warn-bg": "#FFF3D6",
  "color-warn-ink": "#8A6A12",
  "color-warn-border": "#E8CF8B",
  "color-err-bg": "#FBE3E0",
  "color-err-ink": "#7B2A20",
  "color-err-border": "#E5A79F",
  "color-info-bg": "#E2ECF7",
  "color-info-ink": "#2F5D8C",
  "color-info-border": "#B9CFE6",
  "color-off-bg": "#E7E9E2",
  "color-off-ink": "#5B5E54",
  /** 막힘 표시(SCR v0.6 · 미결 ④ Bench 한계 표시와 통일) — Bench 는 인라인 #d64545 */
  "color-port-blocked": "#D64545",
  "radius-sm": "10px",
  "radius-lg": "16px",
};

/**
 * 셸 CSS 가 쓰는 --pf-color-* 이름 → 테마 토큰. --pf-* 는 **별칭**이다: `var(--color-ink, #17332B)` 처럼
 * 테마 토큰을 따라가므로 host 가 --color-ink 를 덮으면 셸 화면도 재로드 없이 따라온다.
 */
const COLOR_TOKEN = {
  brandDeep: "color-ink",
  brand: "color-primary",
  brandBright: "color-accent",
  accent: "color-point",
  accentInk: "color-point-ink",
  surfaceTab: "color-surface-tab",
  surfacePc: "color-surface",
  surface: "color-card",
  surfaceDim: "color-surface-dim",
  border: "color-border",
  ink: "color-ink",
  inkMuted: "color-ink-muted",
  scene: "color-scene",
  selectedBg: "color-selected",
  okBg: "color-ok-bg",
  okInk: "color-ok-ink",
  okBorder: "color-ok-border",
  warnBg: "color-warn-bg",
  warnInk: "color-warn-ink",
  warnBorder: "color-warn-border",
  errBg: "color-err-bg",
  errInk: "color-err-ink",
  errBorder: "color-err-border",
  infoBg: "color-info-bg",
  infoInk: "color-info-ink",
  infoBorder: "color-info-border",
  offBg: "color-off-bg",
  offInk: "color-off-ink",
  portBlocked: "color-port-blocked",
} as const;

type ColorName = keyof typeof COLOR_TOKEN;

/** 색 값(기본 테마) — 이름은 S1 부터 쓰던 것을 유지한다(하위 호환). 값은 {@link DEFAULT_TOKENS} 에서 온다. */
export const colors = Object.fromEntries(
  Object.entries(COLOR_TOKEN).map(([k, tok]) => [k, DEFAULT_TOKENS[tok] as string]),
) as Readonly<Record<ColorName, string>>;

export const typography = {
  fontFamily: "'Pretendard', 'Apple SD Gothic Neo', 'Malgun Gothic', 'Segoe UI', sans-serif",
  weightBold: 900,
  weightSemi: 700,
} as const;

export interface DensityTokens {
  /** 일반 버튼 최소 터치 타깃(px) */
  touchTarget: number;
  /** 리모컨(FS-09) 버튼 최소 터치 타깃(px) */
  touchTargetRemote: number;
  /** 버튼 글자 크기(px) */
  buttonFontSize: number;
  /** 리모컨 버튼 글자 크기(px) */
  remoteFontSize: number;
  /** 버튼 모서리(px) */
  buttonRadius: number;
  /** 본문 글자 크기(px) */
  bodyFontSize: number;
  /** 피코 말풍선 글자 크기(px) */
  bubbleFontSize: number;
  /** 라벨 방식 — SE 는 아이콘 우선, SU 는 텍스트 */
  labelStyle: "icon-first" | "text";
}

export const density: Record<Density, DensityTokens> = {
  se: {
    touchTarget: 64,
    touchTargetRemote: 96,
    buttonFontSize: 16,
    remoteFontSize: 30,
    buttonRadius: 16,
    bodyFontSize: 16,
    bubbleFontSize: 16,
    labelStyle: "icon-first",
  },
  su: {
    touchTarget: 44,
    touchTargetRemote: 44,
    buttonFontSize: 14,
    remoteFontSize: 16,
    buttonRadius: 12,
    bodyFontSize: 14,
    bubbleFontSize: 14,
    labelStyle: "text",
  },
};

/**
 * 학년 → 밀도. SCR v0.5: 학년 클레임 1–3 → SE · 4–6 → SU · 미상 → SU.
 * FS-01 에서 확정해 전 화면에 전파한다.
 */
export function densityForGrade(grade: number | null | undefined): Density {
  if (grade != null && Number.isInteger(grade) && grade >= 1 && grade <= 3) return "se";
  return "su";
}

/** 세션 API 의 gradeBand 클레임(SE/SU/미상). */
export type GradeBand = "SE" | "SU" | null | undefined;

/**
 * 세션 gradeBand → 밀도. 제어 평면이 학년 클레임을 SE/SU 로 판정해 내려 준다(SR-AUTH-02).
 * 미상(null)·알 수 없는 값 → SU (SCR v0.6 "학년 정보가 없으면 SU").
 */
export function densityForGradeBand(band: GradeBand | string): Density {
  return band === "SE" ? "se" : "su";
}

/** 포커스 링 — 두 밀도 공통(SR-NFR-06 키보드 접근). */
export const focusRing = { width: 3, offset: 2, color: colors.accent };

const kebab = (s: string) => s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
const ref = (token: string) => `var(--${token}, ${DEFAULT_TOKENS[token] ?? ""})`;

/**
 * CSS 사용자 속성 맵(--pf-*) — 셸이 :root 에 적용한다. 색·모서리는 테마 토큰(--color-* · --radius-*)을 가리키는
 * 별칭이고, 밀도 값(터치 타깃·글자 크기)만 여기서 정해진다.
 */
export function cssVariables(d: Density): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [k, tok] of Object.entries(COLOR_TOKEN)) vars[`--pf-color-${kebab(k)}`] = ref(tok);
  vars["--pf-font-family"] = typography.fontFamily;
  vars["--pf-focus-ring"] = `${focusRing.width}px solid ${ref("color-point")}`;
  vars["--pf-focus-offset"] = `${focusRing.offset}px`;
  const t = density[d];
  vars["--pf-touch-target"] = `${t.touchTarget}px`;
  vars["--pf-touch-target-remote"] = `${t.touchTargetRemote}px`;
  vars["--pf-button-font-size"] = `${t.buttonFontSize}px`;
  vars["--pf-remote-font-size"] = `${t.remoteFontSize}px`;
  vars["--pf-button-radius"] = ref(d === "se" ? "radius-lg" : "radius-md");
  vars["--pf-body-font-size"] = `${t.bodyFontSize}px`;
  vars["--pf-bubble-font-size"] = `${t.bubbleFontSize}px`;
  return vars;
}

// ── 테마 적용 (Bench 이식) ───────────────────────────────────────────────
// 출처: picnic-bench apps/bench-web/src/bridge/theme.ts @62f4f03 (applyTokens · applyDefaultTheme).
// **미수신이 정상 상태다** — 기본 테마가 먼저 서 있고 host(Campus) 토큰은 그 위에 덮인다. 재로드하지 않는다.

/** CSS 변수를 심을 수 있는 대상(HTMLElement — 테스트는 가짜로) */
export interface StyleTarget {
  style: { setProperty(name: string, value: string): void };
}

/** 토큰 이름 규칙 — host 가 준 값이라도 아무 이름이나 심지 않는다. */
const NAME = /^[a-z][a-z0-9-]{0,63}$/;
/** 값에 넣을 수 없는 것: 닫는 괄호·세미콜론·주석 — CSS 선언을 빠져나가는 통로다. */
const UNSAFE = /[;{}]|\/\*|\*\//;

/**
 * 토큰을 CSS 변수(--이름)로 적용한다. 이름·값이 규칙 밖이면 그 항목만 버린다.
 * @returns 실제로 적용된 개수. 0 이면 기본 테마 그대로다.
 */
export function applyTokens(tokens: Record<string, string>, root: StyleTarget): number {
  let applied = 0;
  for (const [name, value] of Object.entries(tokens ?? {})) {
    if (!NAME.test(name)) continue;
    if (typeof value !== "string" || value.length > 128 || UNSAFE.test(value)) continue;
    root.style.setProperty(`--${name}`, value);
    applied++;
  }
  return applied;
}

/** 부팅 시 1회 — 기본 테마를 세운다. host 가 있으면 나중에 덮인다. */
export function applyDefaultTheme(root: StyleTarget): void {
  applyTokens({ ...DEFAULT_TOKENS }, root);
}
